import type {
  ApiErrorBody,
  CreateDeliverable,
  CreateEpic,
  CreateGoal,
  CreateTask,
  Deliverable,
  Epic,
  Me,
  Metadata,
  MondayConnectionStatus,
  PatchDeliverable,
  PatchEpic,
  PatchGoal,
  PatchTask,
  ProductGoal,
  RoadmapSnapshot,
  Task,
} from '@mona-roadmap/domain';
import { ROUTES } from '@mona-roadmap/domain';

export type ApiErrorCode =
  | 'UNAUTHENTICATED'
  | 'FORBIDDEN'
  | 'VALIDATION'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'MONDAY_NOT_CONNECTED'
  | 'MONDAY_RATE_LIMITED'
  | 'UPSTREAM'
  | 'NETWORK'
  | 'UNKNOWN';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: ApiErrorCode,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Server-side current values of the fields that conflicted (409). */
  get conflictCurrent(): Record<string, unknown> | null {
    if (this.code !== 'CONFLICT') return null;
    const d = this.details;
    if (d && typeof d === 'object' && 'current' in d) {
      const c = (d as { current: unknown }).current;
      if (c && typeof c === 'object') return c as Record<string, unknown>;
    }
    return null;
  }

  get retryAfterSeconds(): number | null {
    const d = this.details;
    if (d && typeof d === 'object' && 'retryAfterSeconds' in d) {
      const n = Number((d as { retryAfterSeconds: unknown }).retryAfterSeconds);
      return Number.isFinite(n) ? n : null;
    }
    return null;
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

/** Human readable message for any thrown value. */
export function describeError(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === 'CONFLICT') return 'This item was changed by someone else. The latest values have been loaded.';
    if (e.code === 'MONDAY_RATE_LIMITED') {
      const s = e.retryAfterSeconds;
      return `monday is rate limiting requests${s ? `; retry in ${s}s` : ''}.`;
    }
    if (e.code === 'MONDAY_NOT_CONNECTED') return 'monday is not connected.';
    if (e.code === 'NETWORK') return 'Cannot reach the server. Check your connection.';
    return e.message;
  }
  return e instanceof Error ? e.message : 'Unexpected error';
}

export interface AuthHooks {
  /** Returns a valid bearer token (refreshing when needed) or null when unauthenticated. */
  getToken(): Promise<string | null>;
  /** Called when the server answers 401 after a refresh attempt. */
  onUnauthenticated(): void;
  /** Try to obtain a fresh token after a 401. Returns true when a retry makes sense. */
  refreshToken?(): Promise<boolean>;
}

let apiBase = '';
/** Origin of the API (config.json `apiUrl`); empty in mock mode (same-origin shim). */
export function setApiBase(url: string): void {
  apiBase = url.replace(/\/+$/, '');
}

let hooks: AuthHooks = { getToken: async () => null, onUnauthenticated: () => undefined };
export function setAuthHooks(h: AuthHooks): void {
  hooks = h;
}

async function parseError(res: Response): Promise<ApiError> {
  let body: Partial<ApiErrorBody> | null = null;
  try {
    body = (await res.json()) as Partial<ApiErrorBody>;
  } catch {
    body = null;
  }
  const code = (body?.error?.code as ApiErrorCode | undefined) ?? (res.status === 401 ? 'UNAUTHENTICATED' : res.status === 403 ? 'FORBIDDEN' : 'UNKNOWN');
  return new ApiError(res.status, code, body?.error?.message ?? `Request failed (${res.status})`, body?.error?.details);
}

async function request<T>(method: string, path: string, body?: unknown, retried = false): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = await hooks.getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  let res: Response;
  try {
    res = await fetch(`${apiBase}${path}`, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  } catch {
    throw new ApiError(0, 'NETWORK', 'Network error');
  }
  if (res.status === 401) {
    if (!retried && hooks.refreshToken && (await hooks.refreshToken())) return request<T>(method, path, body, true);
    hooks.onUnauthenticated();
    throw await parseError(res);
  }
  if (!res.ok) throw await parseError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}


export const api = {
  me: () => request<Me>('GET', ROUTES.me),
  roadmap: (refresh = false) => request<RoadmapSnapshot>('GET', refresh ? `${ROUTES.roadmap}?refresh=1` : ROUTES.roadmap),
  metadata: () => request<Metadata>('GET', ROUTES.metadata),
  createDeliverable: (input: CreateDeliverable) => request<{ deliverable: Deliverable }>('POST', ROUTES.deliverables, input),
  patchDeliverable: (id: string, patch: PatchDeliverable) => request<{ deliverable: Deliverable }>('PATCH', ROUTES.deliverable(id), patch),
  createGoal: (input: CreateGoal) => request<{ productGoal: ProductGoal }>('POST', ROUTES.productGoals, input),
  patchGoal: (id: string, patch: PatchGoal) => request<{ productGoal: ProductGoal }>('PATCH', ROUTES.productGoal(id), patch),
  createEpic: (input: CreateEpic) => request<{ epic: Epic }>('POST', ROUTES.epics, input),
  patchEpic: (id: string, patch: PatchEpic) => request<{ epic: Epic }>('PATCH', ROUTES.epic(id), patch),
  tasks: (deliverableId: string) => request<{ tasks: Task[] }>('GET', ROUTES.deliverableTasks(deliverableId)),
  createTask: (deliverableId: string, input: CreateTask) => request<{ task: Task }>('POST', ROUTES.deliverableTasks(deliverableId), input),
  patchTask: (id: string, patch: PatchTask) => request<{ task: Task }>('PATCH', ROUTES.task(id), patch),
  mondayStatus: () => request<MondayConnectionStatus>('GET', ROUTES.mondayStatus),
  mondayConnect: () => request<{ authorizeUrl: string }>('POST', ROUTES.mondayConnect, {}),
  mondayCallback: (input: { code: string; state: string }) => request<unknown>('POST', ROUTES.mondayCallback, input),
};

